import { useState } from "react";

interface BaseItem {
  id: string;
  label: string;
}

interface DefaultListItemProps<T extends BaseItem> {
  item: T;
  isSelected: boolean;
  onClick: () => void;
}

const DefaultListItem = <T extends BaseItem>({
  item,
  isSelected,
  onClick,
}: DefaultListItemProps<T>) => {
  return (
    <button
      onClick={onClick}
      className={`
        relative flex h-12 w-full items-center justify-start
        rounded-[24px] px-6 py-4 shadow-[0px_1.272px_15.267px_0px_rgba(0,0,0,0.05)]
        transition-colors duration-200
        ${isSelected ? "bg-brand-color/25" : "bg-white/40"}
      `}
    >
      <p
        className={`
          font-sans text-[16px] leading-[22px]
          ${isSelected ? "font-medium" : "font-normal"}
          text-[#1b1b1b]
        `}
        style={{ fontVariationSettings: "'opsz' 14" }}
      >
        {item.label}
      </p>
    </button>
  );
};

interface ActionListPickerProps<T extends BaseItem> {
  items?: T[];
  onSelectionChange?: (selectedIds: string[]) => void;
  multiSelect?: boolean;
  ItemComponent?: React.ComponentType<{
    item: T;
    isSelected: boolean;
    onClick: () => void;
  }>;
  selectedIds?: string[];
}

export const ActionListPicker = <T extends BaseItem>({
  items = [] as T[],
  onSelectionChange,
  multiSelect = true,
  ItemComponent = DefaultListItem,
  selectedIds = [],
}: ActionListPickerProps<T>) => {
  const [selectedIdsState, setSelectedIdsState] = useState<Set<string>>(
    () => new Set(selectedIds)
  );

  const handleItemClick = (itemId: string) => {
    const newSelectedIds = new Set(selectedIdsState);

    if (multiSelect) {
      if (newSelectedIds.has(itemId)) {
        newSelectedIds.delete(itemId);
      } else {
        newSelectedIds.add(itemId);
      }
    } else {
      newSelectedIds.clear();
      newSelectedIds.add(itemId);
    }

    setSelectedIdsState(newSelectedIds);
    onSelectionChange?.(Array.from(newSelectedIds));
  };

  return (
    <div className="flex w-full flex-col gap-6">
      {items.map((item) => {
        const isSelected = selectedIdsState.has(item.id);

        return (
          <ItemComponent
            key={item.id}
            item={item}
            isSelected={isSelected}
            onClick={() => handleItemClick(item.id)}
          />
        );
      })}
    </div>
  );
};
